// src/client/renderer/shaders/comic-bubble.ts
//
// Comic-bubble shader factory (Plan 14b, Task 2.1).
//
// Produces a fresh THREE.ShaderMaterial per call so different bubble meshes
// get independent uniform state (uTime can advance per-mesh independently).
//
// Material: transparent: true + side: THREE.DoubleSide so the bubble quad is
// visible from any camera angle and supports alpha discard.
//
// Context7 (mrdoob/three.js):
//   "material = new THREE.ShaderMaterial({ uniforms, vertexShader,
//    fragmentShader, side: THREE.DoubleSide, transparent: true });"
// Source: https://github.com/mrdoob/three.js/blob/dev/examples/webgl_clipculldistance.html

import * as THREE from 'three';
import type { ShaderFactory } from './types';
import { VERTEX as BUBBLE_VERTEX, FRAGMENT as BUBBLE_FRAGMENT } from './comic-bubble.glsl';

export interface ComicBubbleUniforms {
  /** Per-frame time in seconds. Slight rotation jitter via uTime * 0.5. Default: 0. */
  uTime: THREE.IUniform<number>;
  /** Bubble fill color (e.g., 0xffeb3b yellow for BOOM). Default: (1, 1, 0). */
  uColor: THREE.IUniform<THREE.Color>;
  /** Outline color (typically black 0x000000 for comic-book look). Default: (0, 0, 0). */
  uOutlineColor: THREE.IUniform<THREE.Color>;
  /** Number of spikes around the bubble perimeter (integer-like float). Default: 12. */
  uSpikes: THREE.IUniform<number>;
  /** Spike amplitude (0..1; 0 = circle, 1 = star). Default: 0.15. */
  uIrregularity: THREE.IUniform<number>;
  /** Overall opacity (0..1). Default: 1. */
  uOpacity: THREE.IUniform<number>;
}

export const createComicBubbleShader: ShaderFactory = () => {
  const uniforms: ComicBubbleUniforms = {
    uTime: { value: 0 },
    uColor: { value: new THREE.Color(1, 1, 0) },
    uOutlineColor: { value: new THREE.Color(0, 0, 0) },
    uSpikes: { value: 12 },
    uIrregularity: { value: 0.15 },
    uOpacity: { value: 1 },
  };
  return new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as Record<string, THREE.IUniform<unknown>>,
    vertexShader: BUBBLE_VERTEX,
    fragmentShader: BUBBLE_FRAGMENT,
    transparent: true,
    side: THREE.DoubleSide,
  });
};
